import { useState } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const faqs = [
    {
      question: 'How long does it take to build a website?',
      answer: 'Most websites are designed and launched within 2-3 weeks. The timeline depends on the complexity of your project and how quickly you provide feedback and content. We work efficiently to get you online fast without compromising quality.',
    },
    {
      question: 'Do I need my own domain and hosting?',
      answer: 'No worries if you don\'t have these yet. We can help you register a domain and set up hosting as part of our service. If you already have a domain, we can work with that too.',
    },
    {
      question: 'Can I cancel anytime?',
      answer: 'Yes, absolutely. There are no long-term contracts. You can cancel your monthly service with 30 days notice. We believe in earning your business every month through great service and results.',
    },
    {
      question: 'What if I already have a website?',
      answer: 'Perfect! We can audit your existing site and either redesign it completely or make strategic improvements to boost performance and conversions. Many of our clients come to us with outdated or underperforming websites.',
    },
    {
      question: 'What\'s included in monthly updates?',
      answer: 'Your monthly package includes content updates, security patches, performance optimisation, bug fixes, and minor design tweaks. The number of hours varies by package. Larger changes or new features can be discussed separately.',
    },
    {
      question: 'Do you provide training on how to use my website?',
      answer: 'Yes! We provide clear documentation and training so you can make basic updates yourself if you want to. But if you prefer, we handle everything for you as part of your monthly service.',
    },
    {
      question: 'What makes your service different from other web design agencies?',
      answer: 'Unlike traditional agencies that build your site and disappear, we provide ongoing support and maintenance. You get a dedicated team that knows your business and is always available to help. Plus, our transparent monthly pricing means no surprise bills.',
    },
    {
      question: 'Do you offer SEO services?',
      answer: 'Yes! All our websites are built with SEO best practices from the ground up. We also offer ongoing SEO optimization as part of our monthly packages, including keyword research, content optimization, and performance tracking.',
    },
    {
      question: 'Can you integrate third-party tools and platforms?',
      answer: 'Absolutely. We regularly integrate CRM systems, email marketing platforms, booking systems, payment gateways, and more. If you have specific tools you need to connect, we can make it happen.',
    },
    {
      question: 'What happens if my website goes down?',
      answer: 'We monitor all our client websites 24/7. If there\'s an issue, we\'re notified immediately and work to resolve it as quickly as possible. Our monthly packages include priority support for any technical emergencies.',
    },
    {
      question: 'Do you work with businesses outside your local area?',
      answer: 'Yes! While we love working with local businesses, we serve clients across the country and internationally. All our communication and project management is done remotely, making distance a non-issue.',
    },
    {
      question: 'What if I need changes after my website launches?',
      answer: 'That\'s exactly what our monthly support packages are for. You can request updates, changes, and improvements anytime. We\'ll handle everything for you - no need to learn complicated website builders or worry about breaking anything.',
    },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await supabase.functions.invoke('handle-contact-form', {
        body: {
          name: formData.get('name'),
          email: formData.get('email'),
          phone: formData.get('phone') || undefined,
          website: formData.get('website') || undefined,
          message: formData.get('message'),
          formType: 'contact',
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      setFormStatus('success');
      form.reset();
      setTimeout(() => {
        setShowContactForm(false);
        setFormStatus('idle');
      }, 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a0033]/30 via-transparent to-[#001a33]/30"></div>
        </div>

        <div className="relative z-10 max-w-[1200px] mx-auto px-4 md:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-white/70 max-w-[700px] mx-auto">
            Everything you need to know about our web design and support services. Can't find what you're looking for? Get in touch with us.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-20">
        <div className="max-w-[900px] mx-auto px-4 md:px-8">
          <div className="space-y-3 md:space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`rounded-xl md:rounded-2xl bg-[#1a1a1a] transition-all duration-300 overflow-hidden border ${
                  openIndex === index ? 'bg-[#1f1f1f] border-[#0066ff]/30' : 'border-transparent hover:border-[#0066ff]/30'
                }`}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-5 md:px-8 py-5 md:py-6 flex items-center justify-between text-left cursor-pointer"
                >
                  <span className="text-base md:text-lg font-semibold text-white pr-4">{faq.question}</span>
                  <i
                    className={`text-[#0066ff] text-xl md:text-2xl transition-transform duration-300 flex-shrink-0 ${
                      openIndex === index ? 'ri-subtract-line' : 'ri-add-line'
                    }`}
                  ></i>
                </button>

                <div
                  className={`transition-all duration-300 ease-in-out ${
                    openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-5 md:px-8 pb-5 md:pb-6">
                    <p className="text-base text-white/70 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-tr from-[#1a0033]/20 via-transparent to-[#001a33]/20"></div>
        </div>

        <div className="relative z-10 max-w-[900px] mx-auto px-4 md:px-8">
          <div className="bg-gradient-to-br from-[#8b2bd9] to-[#d946ef] rounded-3xl p-8 md:p-16 text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
              Have a Question You Want Answered?
            </h2>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-8 md:mb-10">
              Can't find the answer you're looking for? Our team is here to help. Get in touch and we'll respond within 48 hours.
            </p>
            
            <button
              onClick={() => setShowContactForm(true)}
              className="px-8 md:px-10 py-4 md:py-5 bg-white text-[#8b2bd9] font-bold rounded-xl hover:bg-white/90 transition-all duration-300 transform hover:scale-105 cursor-pointer text-base md:text-lg whitespace-nowrap shadow-2xl"
            >
              Get in Touch
            </button>

            <p className="text-white/80 text-sm mt-6">
              <i className="ri-time-line mr-2"></i>
              Response within 48 hours • No obligation • Free consultation
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Popup */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-lg rounded-3xl p-6 md:p-8 max-w-2xl w-full max-h-[85vh] overflow-y-auto border border-purple-500/20">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl md:text-3xl font-bold text-white">Get In Touch</h3>
              <button
                onClick={() => setShowContactForm(false)}
                className="text-gray-400 hover:text-white text-2xl cursor-pointer transition-colors duration-300 w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            {formStatus === 'success' ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-check-line text-green-400 text-4xl"></i>
                </div>
                <h4 className="text-2xl font-bold text-white mb-3">Message Sent Successfully!</h4>
                <p className="text-white/70">We'll get back to you within 48 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="faq-contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-white mb-2 text-sm font-semibold">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      disabled={formStatus === 'submitting'}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition-colors duration-300 disabled:opacity-50"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-white mb-2 text-sm font-semibold">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      disabled={formStatus === 'submitting'}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition-colors duration-300 disabled:opacity-50"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-white mb-2 text-sm font-semibold">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    disabled={formStatus === 'submitting'}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition-colors duration-300 disabled:opacity-50"
                    placeholder="+44 7700 900000"
                  />
                </div>

                <div>
                  <label htmlFor="website" className="block text-white mb-2 text-sm font-semibold">
                    Current Website (if any)
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    disabled={formStatus === 'submitting'}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition-colors duration-300 disabled:opacity-50"
                    placeholder="https://yourwebsite.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-white mb-2 text-sm font-semibold">
                    Tell us about your project *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    disabled={formStatus === 'submitting'}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition-colors duration-300 resize-none disabled:opacity-50"
                    placeholder="What are your goals for this website? What challenges are you facing?"
                  ></textarea>
                  <p className="text-white/40 text-xs mt-2">Maximum 500 characters</p>
                </div>

                {formStatus === 'error' && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                    <p className="text-red-400">Something went wrong. Please try again.</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-500 text-white font-bold rounded-xl hover:from-purple-500 hover:to-blue-400 transition-all duration-300 transform hover:scale-105 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
